/**
 * scoring.js - GAME-LEVEL-ENGINE colour scoring
 *
 * Implements section 7 of docs/GAME-LEVEL-ENGINE.md:
 *   7.4  box colour = first-attempt successes over the last 10 plays
 *   7.5  level colour = the LOWEST box colour in that level
 *   7.6  fewer than 10 plays = not yet assessed
 *
 * PURE. No state, no DOM, no I/O. Every function takes data and returns data.
 *
 * ---------------------------------------------------------------------------
 * RECOGNITION CONFIDENCE (audit finding B44 - CLOSED)
 *
 * B44 was that client/src/hooks/useSpeechRecognition.js set
 * `maxAlternatives = 3`, read only `result[0].transcript`, and threw the
 * confidence and the alternatives away - fetching the single most load-bearing
 * input to the colour system from the browser and discarding it. That is
 * fixed: the hook's `readRecognitionEvent()` emits both, `browserStage.js`
 * forwards them, `trialRecorder.build()` carries them, `trialLoop.js` puts
 * them on the payload as `recognition`, and `POST /api/engine/attempts` hands
 * them here.
 *
 * SO DO NOT PASS `confidence: null` TO GET AROUND THIS. An earlier version of
 * this header told callers to, because at the time nothing upstream supplied a
 * real score. Passing null now silently disables the gate for that game, and
 * there is no runtime symptom: bad-microphone trials simply start colouring
 * boxes red. Pass what the recogniser actually returned. null is correct only
 * when the BROWSER reported nothing, which the hook already encodes.
 *
 * This module consumes that score. `assessRecognition()` compares the
 * confidence against the phase's strictness floor (framework.js
 * STRICTNESS_BANDS[...].rules.minConfidence) and classifies the result as
 * reliable or not.
 *
 * An UNRELIABLE result is scored as INCONCLUSIVE, not as a failure. It is
 * excluded from the 10-play window entirely. This is required by section 2.4
 * (errorless learning): a child must never watch their chart go red because
 * of a bad microphone, a sibling talking, or a browser that under-reports
 * confidence. The game should re-prompt without penalty.
 *
 * Browsers that do not populate confidence report `undefined` or exactly 0
 * on final results (Firefox, some Safari builds). Both are treated as
 * UNKNOWN, not as zero confidence - the gate cannot run on absent data, so
 * the result is trusted and flagged `confidenceMissing`. Without this, the
 * gate would discard every attempt on those browsers.
 *
 * WHAT THE GATE DOES NOT DO. It catches results the recogniser is unsure of.
 * It cannot catch a CONFIDENT mis-hearing - "sock" returned as an obscenity at
 * 0.996 - because nothing in the confidence number is wrong there. That class
 * is held off by engine/language.js and by choosing words with no severe
 * neighbour, not by this. See CLAUDE.md section 5.
 * ---------------------------------------------------------------------------
 */

import {
  COLORS,
  COLOR_ORDER,
  SCORING_WINDOW,
  BOX_STATES,
  COMPONENT_ROLES,
  getBoxesForLevel,
  getAssessableBoxes,
  boxState,
  getStrictness,
  getAllLevels,
  parseBoxId,
} from './framework.js';

/* =========================================================================
 * COLOUR
 * ========================================================================= */

/**
 * Map a count of first-attempt successes to a colour (section 7.4).
 * Assumes a full 10-play window; callers should use scoreBox() rather than
 * this directly, so the "fewer than 10 plays" rule is applied.
 *
 * @param {number} successes 0-10
 * @returns {string} colour id
 */
export function colorForSuccesses(successes) {
  const n = Math.max(0, Math.min(SCORING_WINDOW, Math.round(successes)));
  for (const id of COLOR_ORDER) {
    const c = COLORS[id];
    if (n >= c.min && n <= c.max) return id;
  }
  return 'red';
}

/**
 * Rank a colour for the "lowest wins" rule.
 * @param {string} colorId
 * @returns {number|null} 0 (gray) .. 5 (green); null for the non-colour states
 *          'observational' and 'not_instrumented', which have no rank because
 *          they are not positions on the scale.
 */
export function colorRank(colorId) {
  if (isNonColorState(colorId)) return null;
  return (COLORS[colorId] || COLORS.gray).rank;
}

/** True for the two states that are not colours and never read as failure. */
export function isNonColorState(id) {
  return id === BOX_STATES.OBSERVATIONAL || id === BOX_STATES.NOT_INSTRUMENTED;
}

/**
 * The lowest colour in a set (section 7.5).
 *
 * Non-colour states are filtered out before the minimum is taken. An
 * observational box must never drag a level down - it is not a poor result,
 * it is an absent measurement, and ranking it at all would make the levels
 * containing it permanently un-masterable. See scoreLevel().
 *
 * Gray still ranks below red, so a level with a genuinely unassessed box
 * reads as unassessed.
 *
 * @param {string[]} colorIds
 * @returns {string} colour id; 'gray' when nothing rankable is present
 */
export function lowestColor(colorIds) {
  const ranked = (colorIds || []).filter((c) => !isNonColorState(c));
  if (!ranked.length) return 'gray';
  return ranked.reduce((low, c) => (colorRank(c) < colorRank(low) ? c : low), ranked[0]);
}

/* =========================================================================
 * RECOGNITION GATE
 * ========================================================================= */

export const CONFIDENCE_UNKNOWN = 'unknown';

/**
 * Decide whether a speech recognition result is trustworthy enough to score.
 *
 * @param {object} input
 * @param {number} input.phase          1-10, selects the strictness floor
 * @param {number|null} [input.confidence] SpeechRecognitionAlternative.confidence,
 *        0..1. null/undefined/0 are treated as UNKNOWN - see header.
 * @param {string} [input.transcript]   what was heard, for the returned record
 * @param {Array<{transcript:string, confidence:number}>} [input.alternatives]
 *        the other results maxAlternatives asked for; retained for logging
 *        and future re-scoring, not used by the gate.
 * @returns {{reliable:boolean, confidence:number|null, confidenceMissing:boolean,
 *            minConfidence:number, band:string, transcript:string,
 *            alternatives:Array, reason:string}}
 */
export function assessRecognition({ phase, confidence, transcript = '', alternatives = [] }) {
  const strictness = getStrictness(phase);
  const min = strictness ? strictness.rules.minConfidence : 0;
  const band = strictness ? strictness.id : CONFIDENCE_UNKNOWN;

  const missing = confidence === null || confidence === undefined || confidence === 0;
  const value = missing ? null : Number(confidence);

  if (missing) {
    return {
      reliable: true,
      confidence: null,
      confidenceMissing: true,
      minConfidence: min,
      band,
      transcript,
      alternatives,
      reason: 'Browser reported no confidence score; result trusted, gate not applied.',
    };
  }

  const reliable = value >= min;
  return {
    reliable,
    confidence: value,
    confidenceMissing: false,
    minConfidence: min,
    band,
    transcript,
    alternatives,
    reason: reliable
      ? `Confidence ${value.toFixed(2)} meets the ${band} floor of ${min}.`
      : `Confidence ${value.toFixed(2)} is below the ${band} floor of ${min}; attempt is inconclusive and will not be scored.`,
  };
}

/* =========================================================================
 * ONE ATTEMPT
 * ========================================================================= */

/**
 * Turn one speech attempt into per-box outcomes ready to persist.
 *
 * The game supplies its own judgement of each component - only the game knows
 * whether the carrier phrase was present or the sequence was logical. This
 * function does not judge speech; it validates the coverage, applies the
 * confidence gate, and marks which outcomes count toward the 10-play window.
 *
 * Section 8.1 rule 3 requires every box of the level to be measured. A missing
 * component is reported in `errors` and `ok` is false, but the boxes that WERE
 * supplied are still returned so the caller can decide whether to persist a
 * partial record or drop it.
 *
 * @param {object} input
 * @param {number} input.phase
 * @param {number} input.level
 * @param {Object<string, boolean|{success:boolean, heard?:string, expected?:string}>}
 *        input.components keyed by component code ("A".."E")
 * @param {number} [input.attemptNumber=1] 1 = first attempt at this step.
 *        Only attemptNumber === 1 can produce a first-attempt success.
 * @param {object} [input.recognition] passed straight to assessRecognition();
 *        must at least carry `confidence`.
 * @param {Object<string,{applicable:boolean, reason:string}>} [input.applicability]
 *        from judgement.judgeApplicability(). A box marked not applicable on
 *        this trial is still REQUIRED and still recorded - section 8.1 rule 3
 *        does not bend - but it is excluded from the ten-play window rather
 *        than written as a failure. The window is ten APPLICABLE plays.
 *
 *        Only this argument is read. An `applicable` field on a supplied
 *        component is ignored: the framework declares which boxes are
 *        conditional and the engine decides whether the condition was met, for
 *        the same reason capabilities are read from the registry and not from
 *        the request.
 * @returns {{ok:boolean, errors:string[], inconclusive:boolean,
 *            recognition:object, isFirstAttempt:boolean,
 *            boxes:Array<{boxId:string, code:string, success:boolean,
 *                         applicable:boolean, firstAttemptSuccess:boolean,
 *                         counted:boolean}>}}
 */
export function scoreAttempt({
  phase,
  level,
  components = {},
  attemptNumber = 1,
  recognition = {},
  capabilities,
  applicability = {},
}) {
  // Validate against what this game CAN measure, not the full chart row.
  // A game is never asked for an observational box, nor for an instrumentation
  // box it has not declared. See framework.getAssessableBoxes().
  const required = getAssessableBoxes(phase, level, capabilities);
  const errors = [];

  if (!required.length) {
    const rowExists = getBoxesForLevel(phase, level).length > 0;
    return {
      ok: false,
      errors: [
        rowExists
          ? `Phase ${phase} level ${level} has no boxes this game can measure. ` +
            'Every component of this level is observational or needs an ' +
            'instrumentation capability the game has not declared.'
          : `No such level: phase ${phase}, level ${level}.`,
      ],
      inconclusive: true,
      recognition: assessRecognition({ phase, ...recognition }),
      isFirstAttempt: attemptNumber === 1,
      boxes: [],
      nothingAssessable: rowExists,
    };
  }

  const supplied = Object.keys(components);
  const requiredCodes = required.map((b) => b.code);

  for (const code of requiredCodes) {
    if (!(code in components)) {
      errors.push(
        `Missing result for box ${required.find((b) => b.code === code).boxId} (${code}). ` +
        `Section 8.1 rule 3 requires every component of the level to be measured.`
      );
    }
  }
  for (const code of supplied) {
    if (requiredCodes.includes(code)) continue;

    // Distinguish "you sent a box that does not exist here" from "you sent a
    // box that exists but cannot be measured" - very different bugs.
    const inRow = getBoxesForLevel(phase, level).find((b) => b.code === code);
    if (inRow && inRow.observational) {
      errors.push(
        `Component ${code} (${inRow.name}) at phase ${phase} level ${level} is observational ` +
        'and cannot be scored from a speech attempt. Do not submit a result for it.'
      );
    } else if (inRow && inRow.requires) {
      errors.push(
        `Component ${code} (${inRow.name}) needs the "${inRow.requires}" capability, ` +
        'which this game has not declared. Either declare it or stop submitting this box.'
      );
    } else {
      errors.push(
        `Component ${code} is not measured at phase ${phase} level ${level}. ` +
        `Expected exactly: ${requiredCodes.join(', ')}.`
      );
    }
  }

  const rec = assessRecognition({ phase, ...recognition });
  const isFirstAttempt = attemptNumber === 1;
  const inconclusive = !rec.reliable;

  const boxes = required.map((box) => {
    const raw = components[box.code];
    const success = typeof raw === 'object' && raw !== null ? Boolean(raw.success) : Boolean(raw);
    const verdict = applicability[box.code];
    const applicable = !(verdict && verdict.applicable === false);

    return {
      boxId: box.boxId,
      code: box.code,
      name: box.name,
      success,
      // Whether this TRIAL could answer the question this box asks.
      applicable,
      ...(applicable ? {} : { notApplicableReason: verdict.reason, condition: verdict.why }),
      // Only a reliable, applicable, first attempt contributes to the window.
      firstAttemptSuccess: applicable && !inconclusive && isFirstAttempt && success,
      counted: applicable && !inconclusive && isFirstAttempt,
      detail: typeof raw === 'object' && raw !== null ? raw : undefined,
    };
  });

  return {
    ok: errors.length === 0,
    errors,
    inconclusive,
    recognition: rec,
    isFirstAttempt,
    boxes,
  };
}

/* =========================================================================
 * DID THE ATTEMPT SUCCEED?
 *
 * A different question from "what colour is each box", and it took a simulated
 * session to notice they had been treated as the same one.
 *
 * Section 8.4 paces a child on their success rate and never defines success,
 * so it was read as "every box of this level succeeded". At Phase 2 that is
 * wrong in a way that traps a child: A is "said the word", B and C are "and
 * needed less help", so a child placed at the full model by section 9.2 who
 * says the word perfectly every time scores A true, B false, C false - 0% -
 * and is told to step back a level. Down to Phase 1, where they succeed, are
 * advanced, and return to Phase 2 to be sent away again, forever.
 *
 * So pacing reads every assessable box EXCEPT the ones whose role is
 * `independence`. How much help a child needed is what the support ladder is
 * for: it drives section 2.5's fade through `unaided`. It must not also decide
 * whether they are failing, or the two rules fight and the child loses.
 *
 * A box that was not applicable on this trial is excluded too - it asked a
 * question this trial could not answer, and an unanswerable question is not a
 * failed one.
 * ========================================================================= */

/**
 * @param {object} input
 * @param {number} input.phase
 * @param {number} input.level
 * @param {Object<string, boolean|{success:boolean, applicable?:boolean}>} input.components
 * @param {Object<string,boolean>} [input.capabilities]
 * @returns {{success:boolean, considered:string[], excluded:string[], reason:string}}
 */
export function attemptSucceeded({ phase, level, components = {}, capabilities }) {
  const assessable = getAssessableBoxes(phase, level, capabilities);
  const considered = [];
  const excluded = [];
  let success = true;

  for (const box of assessable) {
    const raw = components[box.code];
    const applicable = !(typeof raw === 'object' && raw !== null && raw.applicable === false);

    if (box.role === COMPONENT_ROLES.INDEPENDENCE || !applicable) {
      excluded.push(box.code);
      continue;
    }
    considered.push(box.code);
    const ok = typeof raw === 'object' && raw !== null ? Boolean(raw.success) : Boolean(raw);
    if (!ok) success = false;
  }

  if (!considered.length) {
    // Every box of this level measures independence or was inapplicable. Not a
    // failure: there was nothing here that asks whether the child did the thing.
    return {
      success: false,
      considered,
      excluded,
      reason: 'No box at this level measures what the child produced on this trial.',
    };
  }

  return {
    success,
    considered,
    excluded,
    reason: success
      ? `Produced what phase ${phase} level ${level} asks for (${considered.join(', ')}).`
      : `Did not produce what phase ${phase} level ${level} asks for (${considered.join(', ')}).`
      + (excluded.length ? ` ${excluded.join(', ')} measure how much help was needed and do not decide this.` : ''),
  };
}

/* =========================================================================
 * ATTEMPT NUMBERING
 *
 * Section 7.4 colours a box from FIRST-attempt successes, so `attemptNumber`
 * decides which plays reach the chart at all. It used to be asserted by the
 * game, which put the single most load-bearing field in the chart on the far
 * side of the network from the data that determines it: a game that miscounts
 * corrupts a child's record silently and nothing detects it. It is derived
 * here, from the stored history, for exactly the reason the pacing history is
 * (ENGINE-BUILD.md section 6.2).
 *
 * THE RULE. Walk backwards through the attempts already recorded at this
 * (child, game, phase, level). While the row is another go at the SAME target,
 * and it did not succeed, it was an earlier attempt at this step. Stop at
 * anything else.
 *
 *   - Stop at a different target. Content is drawn from a pool rather than
 *     scripted (section 8.5), so "the same step" is identified by what was
 *     asked for, not by an ordinal - see GAME-01.md section 7.9.
 *   - Stop at a success. The step was completed; a later trial on the same
 *     word is a fresh step, not a fourth attempt at an old one.
 *   - SKIP an inconclusive attempt without counting it. Section 2.4: an
 *     attempt the recogniser could not hear is not an attempt the child made,
 *     and letting one consume attempt 1 means the step contributes nothing to
 *     the chart at all, because attempts 2 and 3 never count either.
 * ========================================================================= */

/**
 * @param {object} input
 * @param {Array<{expected:string, components:object, inconclusive:boolean}>} input.history
 *        attempts at this (child, game, phase, level), MOST RECENT FIRST.
 * @param {string|null} input.expected what this attempt is asking for.
 * @returns {{attemptNumber:number, priorAttempts:number, skippedInconclusive:number,
 *            reason:string}}
 */
export function attemptNumberFor({ history = [], expected }) {
  if (!expected) {
    return {
      attemptNumber: 1,
      priorAttempts: 0,
      skippedInconclusive: 0,
      reason: 'No target was named, so earlier attempts at it cannot be identified. '
            + 'Treated as a first attempt.',
    };
  }

  let priorAttempts = 0;
  let skippedInconclusive = 0;

  for (const row of history) {
    if (!row || row.expected !== expected) break;

    if (row.inconclusive) {
      skippedInconclusive += 1;
      continue;
    }

    const succeeded = Object.values(row.components || {}).every(
      (c) => (c && typeof c === 'object') ? Boolean(c.success) : Boolean(c)
    );
    if (succeeded) break;

    priorAttempts += 1;
  }

  return {
    attemptNumber: priorAttempts + 1,
    priorAttempts,
    skippedInconclusive,
    reason: priorAttempts === 0
      ? `First countable attempt at "${expected}".`
      : `${priorAttempts} earlier countable attempt(s) at "${expected}" did not succeed`
        + (skippedInconclusive ? `, and ${skippedInconclusive} inconclusive one(s) were skipped.` : '.'),
  };
}

/* =========================================================================
 * ONE BOX
 * ========================================================================= */

/**
 * Colour one box from its play history (sections 7.4 and 7.6).
 *
 * @param {object} input
 * @param {string} input.boxId
 * @param {Array<boolean|number|{firstAttemptSuccess:boolean, counted?:boolean}>}
 *        input.plays chronological, OLDEST FIRST. Entries with
 *        `counted === false` are dropped before the window is taken - an
 *        inconclusive recognition, or a trial on which this box was not
 *        applicable. The window is ten COUNTABLE plays.
 * @returns {{boxId:string, color:string, established:boolean,
 *            successes:number, plays:number, windowSize:number,
 *            provisionalColor:string, fillFraction:number, label:string}}
 *
 * `color` is spec-strict: 'gray' until 10 counted plays exist.
 * `provisionalColor` is what the colour WOULD be, scaled to the plays so far -
 *   section 7.6 permits "partial fill based on attempts so far", and the
 *   dashboard needs something to render. Never persist it as the box colour.
 */
export function scoreBox({ boxId, plays = [] }) {
  const counted = plays.filter(
    (p) => !(typeof p === 'object' && p !== null && p.counted === false)
  );

  const asBool = counted.map((p) => {
    if (typeof p === 'object' && p !== null) return Boolean(p.firstAttemptSuccess);
    return Boolean(p);
  });

  const window = asBool.slice(-SCORING_WINDOW);
  const successes = window.filter(Boolean).length;
  const established = window.length >= SCORING_WINDOW;

  // Scale the partial window up to 10 so an early run of successes reads as
  // an early strong signal rather than as red.
  const scaled = window.length ? (successes / window.length) * SCORING_WINDOW : 0;

  return {
    boxId,
    color: established ? colorForSuccesses(successes) : 'gray',
    established,
    successes,
    plays: window.length,
    windowSize: SCORING_WINDOW,
    provisionalColor: window.length ? colorForSuccesses(scaled) : 'gray',
    fillFraction: window.length / SCORING_WINDOW,
    label: established
      ? COLORS[colorForSuccesses(successes)].label
      : `${window.length}/${SCORING_WINDOW} plays`,
  };
}

/* =========================================================================
 * ONE LEVEL
 * ========================================================================= */

/**
 * Colour a whole level.
 *
 * SECTION 7.5, AS SPECIFIED:
 *   "The level's overall color is determined by the LOWEST box color.
 *    A level is only Green when ALL boxes are Green."
 *
 * SECTION 7.5, AS IMPLEMENTED - a deliberate deviation:
 *   The level takes the lowest colour among its SCOREABLE boxes. Observational
 *   boxes (21 of 208) and boxes needing an undeclared instrumentation
 *   capability are excluded from the minimum entirely.
 *
 * WHY. Taken literally, the spec rule makes parts of its own framework
 * unreachable. Phase 1 A "Attends to Model" is "eyes on screen" - a microphone
 * cannot see. Under the literal rule that box stays gray forever, gray is the
 * lowest colour, so every Phase 1 level stays gray forever, so Phase 1 never
 * completes and no child ever progresses off it. The same holds for Phase 8
 * (component A) and Phase 4 (component C, where the recogniser inserts and
 * deletes articles on its own). The literal rule and the sensor reality cannot
 * both be satisfied; something has to give, and it should be the arithmetic
 * rather than the child's progress.
 *
 * WHAT IS PRESERVED. The spirit of 7.5 is that a child cannot skip a weak
 * component. That is intact: every box that CAN be measured still gates the
 * level, and the level is Green only when all of them are Green. Nothing weak
 * is hidden. What changed is that a box nobody can measure no longer
 * masquerades as a weak one.
 *
 * WHAT IS NOT HIDDEN. Excluded boxes are still returned, still counted, and
 * carry a `state` and a reason. The dashboard is expected to render them
 * distinctly - not as a colour, and never as a failure. `observationalBoxes`
 * and `notInstrumentedBoxes` are on every result so a therapist can see
 * exactly how much of the level was actually assessed.
 *
 * @param {object} input
 * @param {number} input.phase
 * @param {number} input.level
 * @param {Object<string, Array>} input.histories keyed by boxId
 * @param {Object<string,boolean>} [input.capabilities] the game's declaration.
 *        Omit for the child-global chart - see framework.getAssessableBoxes().
 * @returns {{phase:number, level:number, color:string, established:boolean,
 *            mastered:boolean, boxes:Array, weakest:object|null,
 *            greenBoxes:number, scoredBoxes:number, totalBoxes:number,
 *            observationalBoxes:number, notInstrumentedBoxes:number,
 *            assessable:boolean}}
 */
export function scoreLevel({ phase, level, histories = {}, capabilities }) {
  const boxes = getBoxesForLevel(phase, level).map((box) => {
    const state = boxState(box, capabilities);

    if (state !== 'scored') {
      return {
        ...box,
        state,
        // Not a colour. Callers must not rank it, and lowestColor() drops it.
        color: state,
        established: false,
        successes: 0,
        plays: 0,
        windowSize: SCORING_WINDOW,
        provisionalColor: state,
        fillFraction: 0,
        label: state === BOX_STATES.OBSERVATIONAL
          ? 'Not measurable from speech'
          : 'Not instrumented by this game',
      };
    }

    return {
      ...box,
      state: 'scored',
      ...scoreBox({ boxId: box.boxId, plays: histories[box.boxId] || [] }),
    };
  });

  const scored = boxes.filter((b) => b.state === 'scored');
  const observationalBoxes = boxes.filter((b) => b.state === BOX_STATES.OBSERVATIONAL).length;
  const notInstrumentedBoxes = boxes.filter((b) => b.state === BOX_STATES.NOT_INSTRUMENTED).length;

  // A level where nothing is scoreable is not gray (unassessed) and certainly
  // not green. Phase 2 is entirely prompt_sequence-dependent, so a game
  // without that capability genuinely cannot assess any of it - say so.
  if (!scored.length) {
    return {
      phase,
      level,
      color: notInstrumentedBoxes ? BOX_STATES.NOT_INSTRUMENTED : BOX_STATES.OBSERVATIONAL,
      established: false,
      mastered: false,
      boxes,
      weakest: null,
      greenBoxes: 0,
      scoredBoxes: 0,
      totalBoxes: boxes.length,
      observationalBoxes,
      notInstrumentedBoxes,
      assessable: false,
    };
  }

  const color = lowestColor(scored.map((b) => b.color));
  const established = scored.every((b) => b.established);
  const greenBoxes = scored.filter((b) => b.color === 'green').length;

  const weakest = scored.reduce(
    (w, b) => (colorRank(b.color) < colorRank(w.color) ? b : w),
    scored[0]
  );

  return {
    phase,
    level,
    color,
    established,
    mastered: color === 'green',
    boxes,
    weakest,
    greenBoxes,
    scoredBoxes: scored.length,
    totalBoxes: boxes.length,
    observationalBoxes,
    notInstrumentedBoxes,
    assessable: true,
  };
}

/* =========================================================================
 * THE WHOLE CHART
 * ========================================================================= */

/**
 * Score every level in the framework.
 *
 * @param {Object<string, Array>} histories keyed by boxId
 * @param {{capabilities?: Object<string,boolean>}} [opts] a game's declaration.
 *        Omit for the child-global chart.
 * @returns {{levels:Array, byPhase:Object, phaseStatus:Object, summary:object}}
 */
export function scoreChart(histories = {}, opts = {}) {
  const { capabilities } = opts;

  const levels = getAllLevels().map(({ phase, level }) =>
    scoreLevel({ phase, level, histories, capabilities })
  );

  const byPhase = {};
  for (const l of levels) {
    (byPhase[l.phase] ||= []).push(l);
  }

  const allBoxes = levels.flatMap((l) => l.boxes);
  const scoredBoxes = allBoxes.filter((b) => b.state === 'scored');
  const greenBoxes = scoredBoxes.filter((b) => b.color === 'green').length;
  const assessedBoxes = scoredBoxes.filter((b) => b.established).length;

  const phaseStatus = {};
  for (const [phase, rows] of Object.entries(byPhase)) {
    const assessableRows = rows.filter((r) => r.assessable);
    const anyPlayed = rows.some((r) => r.boxes.some((b) => b.plays > 0));

    phaseStatus[phase] = !assessableRows.length
      // Every level of this phase is unmeasurable for this game. Not "not
      // started" - the child could play it forever and nothing would record.
      ? 'not_assessable'
      : assessableRows.every((r) => r.mastered)
        ? 'complete'
        : anyPlayed
          ? 'in_progress'
          : 'not_started';
  }

  return {
    levels,
    byPhase,
    phaseStatus,
    summary: {
      totalBoxes: allBoxes.length,
      // The denominator for progress is what CAN be scored, not all 208.
      // Dividing by 208 would cap every child at 90% forever, because 21 boxes
      // are permanently unscoreable - a progress bar that can never fill is
      // worse than no progress bar.
      scoreableBoxes: scoredBoxes.length,
      observationalBoxes: allBoxes.filter((b) => b.state === BOX_STATES.OBSERVATIONAL).length,
      notInstrumentedBoxes: allBoxes.filter((b) => b.state === BOX_STATES.NOT_INSTRUMENTED).length,
      assessedBoxes,
      greenBoxes,
      masteredLevels: levels.filter((l) => l.mastered).length,
      assessableLevels: levels.filter((l) => l.assessable).length,
      totalLevels: levels.length,
      progressPercent: scoredBoxes.length
        ? Math.round((greenBoxes / scoredBoxes.length) * 100)
        : 0,
    },
  };
}

/**
 * Group a flat list of persisted box_score rows into the `histories` shape the
 * scoring functions take. Rows must already be ordered oldest first.
 *
 * @param {Array<{box_id:string, first_attempt_success:boolean, counted?:boolean}>} rows
 * @returns {Object<string, Array>}
 */
export function historiesFromRows(rows = []) {
  const out = {};
  for (const row of rows) {
    const id = row.box_id || row.boxId;
    if (!parseBoxId(id)) continue;
    (out[id] ||= []).push({
      firstAttemptSuccess: Boolean(row.first_attempt_success ?? row.firstAttemptSuccess),
      counted: row.counted === undefined ? true : Boolean(row.counted),
    });
  }
  return out;
}

export default {
  attemptSucceeded,
  attemptNumberFor,
  colorForSuccesses,
  colorRank,
  isNonColorState,
  lowestColor,
  assessRecognition,
  scoreAttempt,
  scoreBox,
  scoreLevel,
  scoreChart,
  historiesFromRows,
};
